import {Component, OnInit, Output, EventEmitter, Inject, LOCALE_ID, Input, OnDestroy} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StaleDatedCheckFilters } from './stale-dated-check-filters.model';
import { PayGroup } from '../../../shared/models/pay-group.model';
import { PayGroupService } from '../../../services/paygroup.service';
import { map, takeUntil, concatMap, toArray } from 'rxjs/operators';
import { StaleCheckFiltersService } from './stale-dated-check-filters.service';
import { sortBy } from 'lodash';
import { BaseObserver } from '../../../shared/baseobserver';
import { getLocaleNumberSymbol, NumberSymbol } from '@angular/common';
import { StaleDatedCheckDurationType } from '../stale-dated-check-view.model';
import { Subscription } from 'rxjs';
import { UltiCurrencyPipe } from '../../../shared/pipes/ulticurrency.pipe';
import { Observable, from } from 'rxjs';

export class SearchableStaleCheckDuration {
  readonly value: StaleDatedCheckDurationType;
  readonly key: string;
  localizedName: string;

  constructor(type: StaleDatedCheckDurationType) {
    this.value = type;
    this.key = SearchableStaleCheckDuration.getStaleCheckViewTextKey(
      this.value
    );
  }

  static getStaleCheckViewTextKey(
    durationType: StaleDatedCheckDurationType
  ): string {
    switch (durationType) {
      case StaleDatedCheckDurationType.StaleDatedChecks20DaysBefore:
        return 'STALE_DATED_CHECKS.STALE_DATED_CHECKS_NEXT_20_DAYS';
      case StaleDatedCheckDurationType.StaleDatedChecks30DaysAfter:
        return 'STALE_DATED_CHECKS.STALE_DATED_CHECKS_PAST_30_DAYS';
      case StaleDatedCheckDurationType.StaleDatedChecks90DaysAfter:
        return 'STALE_DATED_CHECKS.STALE_DATED_CHECKS_PAST_90_DAYS';
      default:
        return 'STALE_DATED_CHECKS.CURRENT_UPCOMING_STALE_DATED_CHECKS';
    }
  }

  withLocalizedName(localizedName: string) {
    this.localizedName = localizedName;
    return this;
  }
}

@Component({
  selector: 'ssg-stale-check-filters',
  templateUrl: './stale-dated-check-info-filters.component.html',
  styleUrls: ['./stale-dated-check-info-filters.component.less'],
})
export class StaleDatedCheckFiltersComponent  extends BaseObserver  implements OnInit, OnDestroy {
  @Output() toggle: EventEmitter<boolean>;
  private _amountStripRegExp: RegExp;
  private _amountDecimalRegExp: RegExp;
  private _amountMinusRegExp: RegExp;

  public formattedAmount: string;
  allDurationType: SearchableStaleCheckDuration[];

  private _lastFilters: StaleDatedCheckFilters;
  private _subscriptions: Subscription[];
  editFilters: StaleDatedCheckFilters;
  allPayGroups: PayGroup[] = [];

  isEditing = false;
  isInvalid = false;

  get view(): StaleDatedCheckDurationType {
    return this.editFilters && this.editFilters.durationType;
  }

  get selectedDurationType(): SearchableStaleCheckDuration {
    return (
      this.editFilters &&
      this.allDurationType.find((view) => view.value === this.view)
    );
  }
  set selectedDurationType(value: SearchableStaleCheckDuration) {
    this.editFilters.durationType = value.value;
  }

  constructor(
    private readonly _payGroupService: PayGroupService,
    private readonly _staleFiltersService: StaleCheckFiltersService,
    private readonly _ultiCurrencyPipe: UltiCurrencyPipe,
    private readonly _translateService: TranslateService,
    @Inject(LOCALE_ID) locale: string
  ) {
    super();
    const groupSymbol = getLocaleNumberSymbol(
      locale,
      NumberSymbol.CurrencyGroup
    );
    const plusSymbol = getLocaleNumberSymbol(locale, NumberSymbol.PlusSign);
    const decimalSymbol = getLocaleNumberSymbol(
      locale,
      NumberSymbol.CurrencyDecimal
    );
    const minusSymbol = getLocaleNumberSymbol(locale, NumberSymbol.MinusSign);
    this._amountStripRegExp = new RegExp(
      '[$' + '\\' + groupSymbol + '\\' + plusSymbol + ']',
      'g'
    );
    this._amountDecimalRegExp = new RegExp('\\' + decimalSymbol, 'g');
    this._amountMinusRegExp = new RegExp('\\' + minusSymbol, 'g');
    this.toggle = new EventEmitter<boolean>();
  }

  ngOnInit() {
    this.getAllPayGroups();
    this.initQuickFilters();
    this._lastFilters = null;
    this._subscriptions = [];

    this._subscriptions.push(
      this._staleFiltersService
        .getFilters()
        .subscribe((filters) => this._handleFiltersUpdated(filters))
    );
  }

  ngOnDestroy() {
    if (this._subscriptions) {
      for (const subscription of this._subscriptions) {
        subscription.unsubscribe();
      }
      this._subscriptions = null;
    }
  }

  initQuickFilters() {
    const durationTypeList = [];

      durationTypeList.push(
        StaleDatedCheckDurationType.CurrentUpcomingStaleDatedChecks
      );
      durationTypeList.push(
        StaleDatedCheckDurationType.StaleDatedChecks20DaysBefore
      );
      durationTypeList.push(
        StaleDatedCheckDurationType.StaleDatedChecks30DaysAfter
      );
      durationTypeList.push(
        StaleDatedCheckDurationType.StaleDatedChecks90DaysAfter
      );

    this._createStaleChecksViews(durationTypeList).subscribe((durationType) => {
      this.allDurationType = durationType;
    });
  }

  private _createStaleChecksViews(
    duration: StaleDatedCheckDurationType[]
  ): Observable<SearchableStaleCheckDuration[]> {
    const searchableViews = duration.map(
      (type) => new SearchableStaleCheckDuration(type)
    );

    // translating stale dated check duration mentioned in i18
    return from(searchableViews).pipe(
      concatMap((dur) => this._translateService.get(dur.key)),
      map((localizedName, i) =>
        searchableViews[i].withLocalizedName(localizedName)
      ),
      toArray()
    );
  }

  applyEdits() {
    if (this.editFilters.isFormInvalid()) {
      return;
    }

    this._staleFiltersService.updateFilters(this.editFilters);
  }

  clearEditFilters() {
    this.editFilters.reset();
    this.formattedAmount = this.formatAmount(this.editFilters.checkAmount);
    this.applyEdits();
  }

  toggleFilters(value: boolean) {
    this.toggle.emit(value);
  }

  getCode(payGroup: PayGroup) {
    return payGroup.code.trim();
  }

  getAllPayGroups(): void {
    this._payGroupService
      .getPayGroups()
      .pipe(
        map((groups) => sortBy(groups, ['code', 'name'])),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => (this.allPayGroups = result));
  }

  private formatAmount(checkAmount: number) {
    const formattedValue = this._ultiCurrencyPipe.transform(
      checkAmount,
      '1.02',
      undefined,
      true
    );
    return formattedValue === null ? '' : formattedValue;
  }

  amountChanged(value: string) {
    const numText = value
      .replace(this._amountStripRegExp, '')
      .replace(this._amountDecimalRegExp, '.')
      .replace(this._amountMinusRegExp, '-')
      .replace(/\s/g, '');
    const num = Number(numText);
    if (numText === '' || isNaN(num) || num === null) {
      this.editFilters.checkAmount = null;
    } else {
      this.editFilters.checkAmount = Number(num.toFixed(2));
    }
    const formatted = this.formatAmount(this.editFilters.checkAmount);
    if (this.formattedAmount === formatted) {
      this.formattedAmount = formatted + ' ';
    } else {
      this.formattedAmount = formatted;
    }
  }

  private _handleFiltersUpdated(filters: StaleDatedCheckFilters) {
    if (
      this._lastFilters !== null &&
      StaleDatedCheckFilters.compareFilters(this._lastFilters, filters)
    ) {
      return;
    }

    this.editFilters = filters.clone();
    this._lastFilters = filters;
    this.formattedAmount = this.formatAmount(this.editFilters.checkAmount);
  }
}
